import { 
  Book, 
  Users, 
  Calendar, 
  CreditCard, 
  User, 
  CalendarDays, 
  ChartBar, 
  Cog 
} from "lucide-react";
import { GuideSection } from "@/types/guideTypes";

export const guideContent: GuideSection[] = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Book,
    difficulty: "Beginner",
    content: [
      "Welcome to the Hivetivity platform! This comprehensive guide will help you navigate and make the most of our features.",
      "As a business administrator, you'll learn how to efficiently manage your studio and maximize productivity.",
      "Follow these steps to get started with your studio management journey."
    ],
    subsections: [
      {
        title: "Quick Setup Guide",
        content: [
          "1. Set up your organization profile in Settings",
          "2. Configure your billing preferences",
          "3. Add your first staff members",
          "4. Create your class schedule"
        ]
      },
      {
        title: "First Steps Checklist",
        content: [
          "✓ Complete your business profile",
          "✓ Set your operating hours",
          "✓ Add your location information",
          "✓ Customize your payment settings",
          "✓ Set up your first program"
        ]
      }
    ]
  },
  {
    id: "managing-families",
    title: "Managing Families",
    icon: Users,
    difficulty: "Beginner",
    content: [
      "Learn how to effectively manage families and students in your studio.",
      "This section covers everything from adding new families to managing relationships and communication."
    ],
    subsections: [
      {
        title: "Adding New Families",
        content: [
          "1. Navigate to the Families section",
          "2. Click the 'Add Family' button",
          "3. Fill in the family details",
          "4. Add parent information",
          "5. Add student information if applicable"
        ]
      },
      {
        title: "Managing Family Members",
        content: [
          "1. Open the family profile",
          "2. Use the Parents and Students tabs",
          "3. Edit member information as needed",
          "4. Update contact details",
          "5. Manage portal access permissions"
        ]
      },
      {
        title: "Family Communication",
        content: [
          "1. Use the messaging center",
          "2. Set up automated notifications",
          "3. Manage communication preferences",
          "4. Track message history",
          "5. Create family groups"
        ]
      }
    ]
  },
  {
    id: "class-management",
    title: "Class Management",
    icon: Calendar,
    difficulty: "Intermediate",
    content: [
      "Everything you need to know about managing classes and schedules."
    ],
    subsections: [
      {
        title: "Creating Classes",
        content: [
          "1. Go to the Classes section",
          "2. Click 'Add Class'",
          "3. Set class details (name, capacity, schedule)",
          "4. Assign instructors",
          "5. Set pricing and policies"
        ]
      },
      {
        title: "Managing Enrollments",
        content: [
          "1. Select a class from the list",
          "2. Click 'Manage Enrollments'",
          "3. Add or remove students",
          "4. Handle waitlist entries",
          "5. Track attendance"
        ]
      }
    ]
  },
  {
    id: "billing",
    title: "Billing & Payments",
    icon: CreditCard,
    difficulty: "Intermediate",
    content: [
      "Handle all your billing and payment operations efficiently."
    ],
    subsections: [
      {
        title: "Creating Invoices",
        content: [
          "1. Navigate to the Invoices section",
          "2. Click 'Create Invoice'",
          "3. Select the family",
          "4. Add line items",
          "5. Set due date and send"
        ]
      },
      {
        title: "Processing Payments",
        content: [
          "1. Access the Payments Dashboard",
          "2. View outstanding balances",
          "3. Process credit card payments",
          "4. Record cash or check payments",
          "5. Send payment confirmations"
        ]
      }
    ]
  },
  {
    id: "staff-management",
    title: "Staff Management",
    icon: User,
    difficulty: "Advanced",
    content: [
      "Efficiently manage your studio's staff members and their responsibilities."
    ],
    subsections: [
      {
        title: "Adding Staff Members",
        content: [
          "1. Go to the Staff section",
          "2. Click 'Add Staff Member'",
          "3. Enter staff details and role",
          "4. Set permissions",
          "5. Send login credentials"
        ]
      },
      {
        title: "Assigning Classes",
        content: [
          "1. Open the staff member's profile",
          "2. Go to the Classes tab",
          "3. Assign or remove classes",
          "4. Set teaching schedule",
          "5. Manage substitute assignments"
        ]
      }
    ]
  },
  {
    id: "scheduling",
    title: "Scheduling",
    icon: CalendarDays,
    difficulty: "Advanced",
    content: [
      "Master the scheduling features to organize your studio's activities effectively."
    ],
    subsections: [
      {
        title: "Creating Schedules",
        content: [
          "1. Access the Schedule section",
          "2. Choose weekly or monthly view",
          "3. Add new time slots",
          "4. Assign classes to slots",
          "5. Handle schedule conflicts"
        ]
      },
      {
        title: "Managing Seasons",
        content: [
          "1. Navigate to Seasons",
          "2. Create a new season",
          "3. Set registration dates",
          "4. Configure pricing",
          "5. Activate the season"
        ]
      }
    ]
  },
  {
    id: "reporting",
    title: "Reports & Analytics",
    icon: ChartBar,
    difficulty: "Intermediate",
    content: [
      "Access powerful reporting tools to gain insights into your business.",
      "Learn how to generate and interpret different types of reports to make data-driven decisions."
    ],
    subsections: [
      {
        title: "Quick Reports",
        content: [
          "1. Go to the Reports dashboard",
          "2. Choose from predefined report templates",
          "3. Set basic parameters",
          "4. Generate instant insights",
          "5. Export or share results"
        ]
      },
      {
        title: "Custom Reports",
        content: [
          "1. Access the Report Builder",
          "2. Select data points to include",
          "3. Set custom date ranges",
          "4. Apply filters and grouping",
          "5. Save report templates"
        ]
      },
      {
        title: "Financial Analytics",
        content: [
          "1. View revenue breakdowns",
          "2. Analyze payment patterns",
          "3. Track outstanding balances",
          "4. Monitor class profitability",
          "5. Generate projections"
        ]
      }
    ]
  },
  {
    id: "parent-portal",
    title: "Parent Portal Management",
    icon: Cog,
    difficulty: "Intermediate",
    content: [
      "Learn how to configure and manage the parent portal for your families.",
      "Maximize parent engagement and streamline communication through the portal."
    ],
    subsections: [
      {
        title: "Portal Configuration",
        content: [
          "1. Access Portal Settings",
          "2. Set up access permissions",
          "3. Configure payment options",
          "4. Customize portal features",
          "5. Set notification preferences"
        ]
      },
      {
        title: "Parent Resources",
        content: [
          "1. Upload documents and forms",
          "2. Create announcement posts",
          "3. Set up FAQ sections",
          "4. Manage resource libraries",
          "5. Schedule automated updates"
        ]
      }
    ]
  }
];
